import { DestroyRef, ElementRef, forwardRef, inject, Injector } from '@angular/core';
import { NgControl } from '@angular/forms';
import { DynamicFormGroup, getValidationTriggerEvents } from '@common/forms/frontend-shared/dynamic-form-group';
import { takeWhileAlive } from '@core/frontend-shared/utils';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { UiFieldWrapperComponent } from '../field-wrapper';
import { FIELD_WRAPPER_LISTENER } from '../field-wrapper-listener';
import { FormControlNameStandalone } from './form-control-name';
import { InputRefBlockerDirective } from './input-ref-blocker.directive';
import * as i0 from "@angular/core";
export const defaultFormControlStatus = {
  dirty: false,
  touched: false,
  enabled: true,
  focused: false
};
export let InputRefDirective = /*#__PURE__*/(() => {
  class InputRefDirective {
    // since this is a host directive, it will be created BEFORE the host component.
    // so NgControl cannot be injected in constructor. Use injector in ngOnInit to find the control.
    // Also, FormControlName directive, which would provide NgControl, is not standalone, so there are additional timing issues.
    // For that reason there is a hacked-in FormControlNameStandalone directive.
    constructor(injector, host, wrapper, blocker, listener) {
      this.injector = injector;
      this.host = host;
      this.wrapper = wrapper;
      this.blocker = blocker;
      this.listener = listener;
      this.destroyRef = inject(DestroyRef);
      this.focused = false;
      /**
       * blocker helps in a special situation:
       * some input components use other input components to compose their UI.
       * If they wrap any other component using InputRefDirective, this will result in this method being called by each of them.
       * As InputRefDirective cannot actively check parents and neither can receive dynamic configuration (it is a static host directive!),
       * it is being "configurated" by checking for the presence of dynamically assigned InputRefBlockerDirective.
       */
      this.blockWrapperRegistration = false;
      this.classValidatorErrors$ = new BehaviorSubject([]);
      // ngValidationErrors are unused at the moment.
      this.ngValidationErrors$ = new BehaviorSubject([]);
      this.controlStatus$ = new BehaviorSubject(defaultFormControlStatus);
      this.controlValue$ = new BehaviorSubject(undefined);
      this.hasValue$ = this.controlValue$.pipe(takeWhileAlive(), map(v => {
        return typeof v !== 'undefined' && v !== null && v !== '';
      }));
      this.errors$ = combineLatest([this.classValidatorErrors$, this.ngValidationErrors$]).pipe(map(([validatorErrors, ngErrors]) => {
        return [...(validatorErrors || []), ...(ngErrors || [])];
      }));
      try {
        // when trying to inject NgControl and it does not exist, it will always throw an error
        // even if a fallback default value is given! So it must be wrapped in a catch block.
        const injectedFormControl = injector.get(NgControl, null);
        if (injectedFormControl) this.formControl = injectedFormControl;
      } catch (err) {}
    }
    ngOnInit() {
      /**
       * it is possible both a FieldWrapper and a InputRefBlocker is found and injected, BUT it should not block.
       * This situation occurs with nested DOM / nested inputs.
       * Example: An input field contains a dialog which then uses other inputs again.
       * The root input may contain a blocker which is found and injected in the dialog-contained inputRefs.
       * A blocker should only block assignments if it is closer to the inputRef than the found fieldWrapper.
       * Thus, we check the hierarchy of found elements and only set blockWrapperRegistration to true if they are in relevant order.
       */
      if (this.blocker && this.wrapper) {
        const isTargetWrapperInsideBlocker = this.blocker.host.nativeElement.contains(this.wrapper.host.nativeElement);
        if (!isTargetWrapperInsideBlocker) this.blockWrapperRegistration = true;
      }
      if (this.blockWrapperRegistration) return;
      if (this.wrapper) {
        this.wrapper.registerInput(this);
        if (this.listener) this.wrapper.registerListener(this.listener);
      }
      if (!this.formControl) {
        this.formControl = this.injector.get(FormControlNameStandalone, null);
      }
      // add formControlName as visible attribute, helpful for E2E test operations
      // console.log('inputRef',this.formControl.name,this.host.nativeElement)
      this.host.nativeElement.setAttribute('form-control-name', this.formControl.name);
    }
    ngAfterViewInit() {
      if (this.blockWrapperRegistration) return;
      const ngc = this.formControl;
      const control = ngc?.control;
      if (!control) {
        if (!IS_PRODUCTION) console.log('inputRef found no formControl, aborting initialization');
        return;
      }
      getValidationTriggerEvents(control).pipe(takeWhileAlive(this.destroyRef)).subscribe(event => {
        this.updateClassValidatorErrors();
        this.updateControlStatus();
      });
      ngc.control.valueChanges.pipe(takeWhileAlive(this.destroyRef)).subscribe(val => {
        this.updateControlValue(val);
      });
      // // required to push initial status (fixes bug where status.enabled was not correct initially - #861mu07fz)
      this.updateClassValidatorErrors();
      this.updateControlValue(control.value);
      this.updateControlStatus();
    }
    // TODO: needed?
    ngOnChanges() {
      if (this.blockWrapperRegistration) return;
      this.updateControlStatus();
    }
    updateClassValidatorErrors() {
      const fc = this.formControl;
      const parentFormGroup = fc.control.parent;
      // InputRef is used for inputs not assigned to a FormGroup too, so we can only ignore those cases then.
      // while most forms are based on DynamicFormGroup, some forms like e.g. filter forms use classic Forms.
      if (!parentFormGroup || !(parentFormGroup instanceof DynamicFormGroup)) return;
      // console.log('inputRef err update', this.host.nativeElement, fc.control.status, (parentFormGroup as DynamicFormGroup<any>).formErrors?.[fc.name])
      // console.log(fc.control.status,fc.control)
      if (fc.control.status === 'VALID') {
        this.classValidatorErrors$.next([]);
      } else {
        if (parentFormGroup instanceof DynamicFormGroup) {
          this.classValidatorErrors$.next(parentFormGroup.formErrors?.[fc.name] || []);
        } else {
          this.classValidatorErrors$.next(['Error: InputRef detected invalid field but cannot find the root DynamicFormGroup to extract errors from!']);
        }
      }
    }
    updateControlStatus() {
      if (this.blockWrapperRegistration) return;
      this.controlStatus$.next({
        dirty: this.formControl.dirty,
        touched: this.formControl.touched,
        enabled: this.formControl.enabled,
        focused: this.focused
      });
    }
    updateControlValue(value) {
      if (this.blockWrapperRegistration) return;
      if (value !== this.controlValue$.getValue()) this.controlValue$.next(value);
    }
    onBlur() {
      this.focused = false;
      // must be delayed because fields touched state will not have updated yet if called in sync
      setTimeout(() => {
        this.updateControlStatus();
      }, 5);
    }
    onFocus() {
      this.focused = true;
      // must be delayed because fields touched state will not have updated yet if called in sync
      setTimeout(() => {
        this.updateControlStatus();
      }, 5);
    }
    get touched() {
      return this.formControl.touched;
    }
    get dirty() {
      return this.formControl.dirty;
    }
    get enabled() {
      return this.formControl.enabled;
    }
    static {
      this.ɵfac = function InputRefDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || InputRefDirective)(i0.ɵɵdirectiveInject(i0.Injector), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(forwardRef(() => UiFieldWrapperComponent), 8), i0.ɵɵdirectiveInject(forwardRef(() => InputRefBlockerDirective), 8), i0.ɵɵdirectiveInject(forwardRef(() => FIELD_WRAPPER_LISTENER), 8));
      };
    }
    static {
      this.ɵdir = /*@__PURE__*/i0.ɵɵdefineDirective({
        type: InputRefDirective,
        selectors: [["", "inputRef", ""]],
        hostBindings: function InputRefDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("blur", function InputRefDirective_blur_HostBindingHandler() {
              return ctx.onBlur();
            })("focus", function InputRefDirective_focus_HostBindingHandler() {
              return ctx.onFocus();
            });
          }
        },
        features: [i0.ɵɵNgOnChangesFeature]
      });
    }
  }
  return InputRefDirective;
})();